/**
 * LoginLogModal.tsx
 *
 * File-level:
 * - Modal component to display user login logs for admin users.
 * - Features date range filtering (From Date and To Date).
 * - Displays all columns from LoginLog table in a grid.
 * - Each row has a Delete button to remove that record from database.
 *
 * Visual design:
 * - Full-screen project-themed gradient backdrop.
 * - Bold gradient title bar with date filters.
 * - Table header with complementary gradient.
 */

import React, { useEffect, useState } from 'react';
import { X, Trash2, Calendar } from 'lucide-react';
import { API_CONFIG } from '../../config/api.config';
import { trackModalView } from '../../utils/visitorTracking';
import { formatStoredDate } from '@/utils/formatStoredDate';

const API_URL = API_CONFIG.getBackendOrigin();

/**
 * LoginLogRow
 *
 * @description Shape of login log record from database.
 */
export interface LoginLogRow {
  ID: number;
  UserId: string;
  LoginDateTime: string;
  IPAddress: string | null;
  Country: string | null;
  State: string | null;
  City: string | null;
  Latitude: number | null;
  Longitude: number | null;
  ISP: string | null;
  Browser: string | null;
  BrowserVersion: string | null;
  OS: string | null;
  DeviceType: string | null;
  UserAgent: string | null;
  CreatedAt: string;
}

/**
 * LoginLogModalProps
 *
 * @description Props for LoginLogModal component.
 */
interface LoginLogModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called to close the modal */
  onClose: () => void;
}

/**
 * LoginLogModal
 *
 * @description Modal UI that lists login logs with date filtering and delete functionality.
 */
export default function LoginLogModal({ open, onClose }: LoginLogModalProps) {
  const [logs, setLogs] = useState<LoginLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  // Date filters - default to last 30 days
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    let mounted = true;

    // Track modal view
    if (open) {
      trackModalView('Login Log Modal');
    }

    /**
     * fetchLoginLogs
     *
     * @description Fetch login logs from backend API with date filtering.
     */
    async function fetchLoginLogs() {
      setError(null);
      setLoading(true);
      try {
        const params = new URLSearchParams({
          fromDate: fromDate,
          toDate: toDate,
        });
        const res = await fetch(`${API_URL}/api/admin/login-logs?${params}`);
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`${res.status} ${res.statusText} - ${txt}`);
        }
        const json = await res.json();
        const list = Array.isArray(json.data) ? json.data : [];
        if (mounted) {
          setLogs(list);
          setCurrentPage(1); // Reset to first page when data changes
        }
      } catch (err: any) {
        console.error('[LoginLogModal] fetch error', err);
        if (mounted) setError(err?.message ?? 'Failed to fetch login logs');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (open) {
      fetchLoginLogs();
    }

    return () => {
      mounted = false;
    };
  }, [open, fromDate, toDate]);

  /**
   * handleDelete
   *
   * @param id number - ID of the login log record to delete
   * @description Deletes a login log record from the database.
   */
  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this login log entry?')) {
      return;
    }

    setDeleting(id);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/login-logs/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText} - ${txt}`);
      }
      // Remove from local state
      setLogs((prev) => prev.filter((log) => log.ID !== id));
    } catch (err: any) {
      console.error('[LoginLogModal] delete error', err);
      setError(err?.message ?? 'Failed to delete login log');
    } finally {
      setDeleting(null);
    }
  }

  // Calculate pagination
  const totalPages = Math.ceil(logs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedLogs = logs.slice(startIndex, endIndex);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundImage: 'var(--dashboard-bg)',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-log-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title bar with gradient and date filters */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{
            backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <div className="flex items-center gap-4">
            <h2
              id="login-log-modal-title"
              className="text-2xl font-bold text-white"
            >
              Login Log
            </h2>
            <div className="bg-white/20 px-3 py-1 rounded-full text-white text-sm font-semibold">
              {logs.length} {logs.length === 1 ? 'record' : 'records'}
            </div>
          </div>

          {/* Date filters */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-white" />
              <label className="text-white text-sm font-medium">From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-1 rounded-lg border border-white/20 bg-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
                max={toDate}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-white text-sm font-medium">To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-1 rounded-lg border border-white/20 bg-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
                min={fromDate}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-white hover:bg-white/10 rounded-lg p-2 transition"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Table content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-slate-500">Loading login logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-slate-500">No login logs found for the selected date range.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    className="text-white text-sm"
                    style={{
                      backgroundImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    <th className="px-4 py-3 text-left font-semibold">ID</th>
                    <th className="px-4 py-3 text-left font-semibold">User ID</th>
                    <th className="px-4 py-3 text-left font-semibold">Login Date & Time</th>
                    <th className="px-4 py-3 text-left font-semibold">IP Address</th>
                    <th className="px-4 py-3 text-left font-semibold">Country</th>
                    <th className="px-4 py-3 text-left font-semibold">State</th>
                    <th className="px-4 py-3 text-left font-semibold">City</th>
                    <th className="px-4 py-3 text-left font-semibold">Browser</th>
                    <th className="px-4 py-3 text-left font-semibold">OS</th>
                    <th className="px-4 py-3 text-left font-semibold">Device</th>
                    <th className="px-4 py-3 text-center font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log, idx) => (
                    <tr
                      key={log.ID}
                      className={`border-b border-slate-200 hover:bg-slate-50 transition ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                      }`}
                    >
                      <td className="px-4 py-3 text-sm text-slate-700">{log.ID}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 font-mono">{log.UserId}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {log.LoginDateTime ? formatStoredDate(log.LoginDateTime) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 font-mono">{log.IPAddress || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{log.Country || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{log.State || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{log.City || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {log.Browser || '-'} {log.BrowserVersion && `(${log.BrowserVersion})`}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{log.OS || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{log.DeviceType || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(log.ID)}
                          disabled={deleting === log.ID}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white rounded-lg text-xs font-medium transition"
                          aria-label={`Delete login log ${log.ID}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          {deleting === log.ID ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {logs.length > ITEMS_PER_PAGE && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Showing {startIndex + 1} to {Math.min(endIndex, logs.length)} of {logs.length} records
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400 text-slate-700 rounded-lg text-sm font-medium transition"
              >
                Previous
              </button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                      currentPage === page
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400 text-slate-700 rounded-lg text-sm font-medium transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
